import { isFunction, isString, isStringEmpty } from '@mechhive/helpers';
import classNames from 'classnames';
import type { ChangeEvent, ChangeEventHandler, FocusEventHandler, HTMLInputTypeAttribute, InputHTMLAttributes, InvalidEvent } from 'react';
import { useState } from 'react';
import { useTranslate } from '~/hooks/useTranslate';
import type { TranslationKey } from '~/translations.server';
import { Icon, type IconName } from '../ui/Icon';

export enum InputTextSize {
  Default = 'default',
  Small = 'small'
}

type InputTextProps = {
  className?: string;
  name?: string;
  title?: string;
  subTitle?: string;
  type?: HTMLInputTypeAttribute;
  defaultValue?: string | ReadonlyArray<string> | number | undefined;
  value?: string | ReadonlyArray<string> | number | undefined;
  placeholder?: string;
  autoFocus?: boolean;
  invalid?: boolean;
  invalidMessage?: string | null;
  validityMessage?: ( vadilty: string ) => ( string | undefined | void ) | string;
  required?: boolean;
  iconName?: IconName;
  size?: InputTextSize;
  minLength?: number;
  readOnly?: boolean;
  maxLength?: number;
  pattern?: string;
  disabled?: boolean;
  role?: string;
  prefixLabel?: string;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined;
  onFocus?: FocusEventHandler<HTMLInputElement> | undefined;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
}

export const InputText = ( { className, name, title, subTitle, type, defaultValue, value, onChange, placeholder, autoFocus, invalid, invalidMessage, required, iconName, size = InputTextSize.Default, readOnly, onFocus, onBlur, maxLength, pattern, validityMessage, disabled, autoComplete, role, prefixLabel, minLength }: InputTextProps ) => {
  const t = useTranslate();
  const [ validity, setValidity ] = useState<string>();

  const handleChange = ( e: ChangeEvent<HTMLInputElement> ) => {
    if ( isFunction( onChange ) ) {
      onChange( e );
    }

    if ( e.currentTarget.validity.valid === true ) {
      setValidity( undefined );
    }
  }

  const handleInvalid = ( e: InvalidEvent<HTMLInputElement> ) => {
    e.preventDefault();

    if ( e.currentTarget.validity.valid === true ) {
      setValidity( undefined );
      return;
    }

    for ( let prop in e.currentTarget.validity ) {
      if ( e.currentTarget.validity[ prop ] === true ) {
        setValidity( prop );
      }
    }
  }

  return (
    <div className={ classNames( 'flex flex-col gap-3', className ) }>
      <div className={ 'flex flex-col gap-1.5' }>
        { title &&
          <div className={ 'text-[#ABB0C7]' }>
            { title }
          </div>
        }
        { subTitle && 
          <div className={ 'text-[#ABB0C7] text-sm' }>
            { subTitle }
          </div>
        }
      </div>
      <div className='relative flex flex-col z-0'>
        <div className={ 'relative' }>
          { prefixLabel &&
            <div className={ 'absolute flex items-center start-center left-0 inset-y-0 pl-4 pt-px' }>
              <span>{ prefixLabel }</span>
            </div>
          }
          <input
            name={ name }
            type={ type }   
            defaultValue={ defaultValue }
            value={ value }
            onChange={ handleChange }
            onFocus={ onFocus }
            onBlur={ onBlur }
            onInvalid={ handleInvalid }
            placeholder={ placeholder }
            autoFocus={ autoFocus }
            required={ required }
            aria-invalid={ invalid }
            data-size={ size }
            readOnly={ readOnly }
            maxLength={ maxLength }
            disabled={ disabled }
            role={ role }
            minLength={ minLength }
            autoComplete={ autoComplete }
            pattern={ !isStringEmpty( pattern ?? '' ) ? `${pattern}` : type === 'email' ? '^[^@\\s]+@[^@\\s]+\\.[a-zA-Z]{2,}$' : undefined }
            className={ classNames(
              'aria-[invalid="true"]:border-red-500 text-white rounded-[10px] p-4 data-[size=small]:py-3 bg-[rgba(12,16,46,0.5)] border border-[#7C7AFE] hover:border-[#5A7DFB] focus:border-[#5A7DFB] leading-[2px] data-[size=small]:leading-[18px] text-base bg-[#0C102E] bg-opacity-50 shadow-[0px_4px_10px_0px_rgba(90,125,251,0.0),0px_-8px_20px_0px_rgba(255,255,255,0.20)_inset] hover:shadow-[0px_4px_10px_0px_rgba(90,125,251,0.0),0px_-8px_20px_0px_rgba(90,125,251,0.20)_inset] focus:shadow-[0px_4px_10px_0px_rgba(90,125,251,0.40),0px_-8px_20px_0px_rgba(90,125,251,0.20)_inset] font-light w-full outline-none transition-all duration-200 read-only:border-[#25224C] read-only:shadow-[0px_-8px_20px_0px_rgba(255,255,255,0.10)_inset] read-only:cursor-no-drop read-only:hover:border-[#25224C] read-only:focus:border-[#25224C] read-only:hover:shadow-[0px_-8px_20px_0px_rgba(255,255,255,0.10)_inset] read-only:focus:shadow-[0px_-8px_20px_0px_rgba(255,255,255,0.10)_inset]',
              validity ? '!border-[#E16B45]' : '',
              iconName || prefixLabel ? 'pl-16' : 'pl-4',
            ) }/>
          { iconName && 
            <div className={ 'absolute inset-y-0 pl-4 flex items-center justify-center' }>
              <Icon
                name={ iconName }
                className={ 'icon-size-[18px]' } />
            </div>
          }
        </div>
      </div>
      { invalidMessage &&
        <div className={ 'text-red-500 text-pretty' }>{ invalidMessage }</div>
      }
      { validity != null &&
        <div className={ 'text-[#E16B45]' }>
          { ( isFunction( validityMessage ) && isString( validityMessage( validity ) ) ) &&
            validityMessage( validity ) as string
          }
          { isString( validityMessage ) &&
            validityMessage
          }
          { !( isFunction( validityMessage ) && isString( validityMessage( validity ) ) ) && !isString( validityMessage ) &&
            t( ( validity === 'typeMismatch' || validity === 'patternMismatch' ) && type === 'email' ? 'INPUT_VALIDITY_FALLBACK_EMAIL' :  `INPUT_VALIDITY_${validity.toUpperCase()}` as TranslationKey )
          }
        </div>
      }
    </div>
  )
}